<template>
  <fragment>
    <page-head
      small
      :title="requestType.title"
      back-route-path="/cases/case-related-request"
    >
    </page-head>

    <page-content small>
      <div
        v-if="isEditMode"
        class="request-info-root"
      >
        <div v-if="formModel.requestCode">
          <b><span class="label-highlighted">{{ local.lawsuitRequest.requestInfo.requestCode }}</span>
            {{ request.requestCode }}</b>
        </div>
        <div v-if="formModel.requestDate">
          <b><span class="label-highlighted">{{ local.lawsuitRequest.requestInfo.requestDate }}</span>
            {{ request.requestDate | hijriDate }}</b>
        </div>
        <div v-if="formModel.requestStatusName">
          <b><span class="label-highlighted">{{ local.lawsuitRequest.requestInfo.status }}</span> {{ request.status }}</b>
        </div>
      </div>

      <div v-if="isNewMode">
        <v-form
          v-if="requireSearch"
          ref="searchFormRef"
          @submit.prevent="onSearch"
        >
          <naj-box
            :title="local.lawsuitRequest.requestInfo.caseDetails"
            has-footer
          >
            <v-row>
              <v-col cols="3">
                <naj-input
                  :type-id="formFieldType.number"
                  :label="local.caseRelatedRequest.filters.caseCode"
                  :value.sync="searchQuery.caseCode"
                  validates="required|long|max:10"
                  @input="filterChanged"
                ></naj-input>
              </v-col>
              <v-col cols="3">
                <naj-input
                  :type-id="formFieldType.number"
                  :label="local.caseRelatedRequest.filters.year"
                  :value.sync="searchQuery.caseYear"
                  validates="required"
                  @input="filterChanged"
                ></naj-input>
              </v-col>
              <v-col cols="3">
                <naj-input
                  :type-id="formFieldType.selectbox"
                  :label="local.caseRelatedRequest.filters.court"
                  :value.sync="searchQuery.courtId"
                  :items="courts"
                  item-text="name"
                  item-value="id"
                  validates="required"
                  @change="filterCourtChanged"
                ></naj-input>
              </v-col>
              <v-col cols="3">
                <naj-input
                  :type-id="formFieldType.number"
                  :label="local.caseRelatedRequest.filters.identityNumber"
                  :value.sync="searchQuery.socialId"
                  validates="required|long"
                  @input="filterChanged"
                ></naj-input>
              </v-col>
            </v-row>
            <template v-slot:footer>
              <span />
              <naj-button
                outlined
                type="button"
                color="primary"
                icon="mdi-magnify"
                :loading="loading"
                @click="onSearch"
              >
                {{ local.common.search }}
              </naj-button>
            </template>
          </naj-box>
        </v-form>
      </div>

      <naj-box :title="local.lawsuitRequest.requestInfo.caseData">
        <naj-data-table
          no-header
          disable-items-per-page
          :headers="tableHeader"
          :loading="loading"
          :items="formModel.case ? [formModel.case] : []"
          :no-margin-bottom="false"
        ></naj-data-table>
      </naj-box>

      <div v-if="showSelectedPartyToRepresent">
        <!-- التحقق من الطرف المراد تمثيلة-->
        <naj-box :title="local.lawsuitRequest.requestInfo.sectionApplicantToApply">
          <v-row>
            <v-col cols="6">
              <naj-alert type="information">
                <template slot="content">
                  {{ local.lawsuitRequest.requestInfo.socialIdInfo }}
                </template>
              </naj-alert>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="3">
              <naj-input
                :type-id="formFieldType.number"
                :label="local.lawsuitRequest.requestInfo.socialId"
                :value.sync="socialId"
                validates="required|long"
              ></naj-input>
            </v-col>
            <v-col
              cols="3"
              class="d-flex align-center"
            >
              <naj-button
                :disabled="isCheckButtonDisabled"
                outlined
                type="button"
                color="primary"
                icon="mdi-magnify"
                :loading="loading"
                @click="getPartyByCaseCode"
              >
                {{ local.lawsuitRequest.requestInfo.check }}
              </naj-button>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <naj-input
                disabled="true"
                :type-id="formFieldType.text"
                :label="local.lawsuitRequest.requestInfo.partyName"
                :value.sync="partyInfo.name"
              ></naj-input>
            </v-col>
            <v-col cols="2">
              <naj-input
                disabled="true"
                :type-id="formFieldType.text"
                :label="local.lawsuitRequest.requestInfo.socialId"
                :value.sync="partyInfo.identityNumber"
              ></naj-input>
            </v-col>
            <v-col cols="2">
              <naj-input
                disabled="true"
                :type-id="formFieldType.text"
                :label="local.lawsuitRequest.requestInfo.typeOfParty"
                :value.sync="partyInfo.partyType"
              ></naj-input>
            </v-col>
            <v-col cols="2">
              <naj-input
                disabled="true"
                :type-id="formFieldType.text"
                :label="local.lawsuitRequest.requestInfo.role"
                :value.sync="partyInfo.caseRole"
              ></naj-input>
            </v-col>
          </v-row>
        </naj-box>
      </div>

      <div v-if="isValidCaseData && isValidApplicantToApply">
        <v-form
          v-if="formModel.case"
          ref="requestFormRef"
        >
          <div v-if="requestType.showDefaultRequestApplicantSection && formModel.case">
            <naj-box :title="local.caseRelatedRequest.relatedPartyInfo">
              <div class="pa-3 content-wrapper">
                <naj-input
                  :type-id="formFieldType.selectbox"
                  :value.sync="formModel.applicantCasePartyId"
                  :label="local.caseRelatedRequest.relatedPartyInfo"
                  :items="prosecutorOrDefendantCaseParties"
                  item-value="casePartyId"
                  item-text="name"
                  validates="required"
                  :disabled="casePartiesSelectboxDisabled"
                ></naj-input>
              </div>
            </naj-box>
          </div>

          <div v-if="requestType.customComponentName">
            <naj-dynamic-form
              :custom-component-name="requestType.customComponentName"
              :required-data="requestCustomData"
              :form-model="formModel"
              :view-mode="isViewMode"
            ></naj-dynamic-form>
          </div>

          <div
            v-if="requestType.showRequestReasonSection && !isReplyMode"
            class="px-3 content-wrapper"
          >
            <h3
              v-if="isRequestReasonTypeTextarea || isRequestReasonTypeSelectbox"
              class="d-inline-block primary--text font-weight-bold col col-2 mb-4"
            >
              {{ local.caseRelatedRequest.requestReasons }}
            </h3>
            <span
              v-if="isRequestReasonTypeTextarea || isRequestReasonTypeSelectbox"
              class="request-reasons-warning"
            >
              <v-icon color="#FAAD14">
                mdi-alert
              </v-icon>
              {{ local.caseRelatedRequest.requestReasonsWarning }}
            </span>

            <div v-if="isRequestReasonTypeTextarea || isRequestReasonTypeSelectbox">
              <template v-if="formModel.requestReasons.length > 0">
                <naj-box
                  v-for="(requestReason, index) in formModel.requestReasons"
                  :key="index"
                  :title="getMappedIndexReasonText(index)"
                >
                  <v-row>
                    <v-col cols="6">
                      <template v-if="requestReason.isDraft">
                        <naj-input
                          v-if="isRequestReasonTypeSelectbox"
                          :type-id="formFieldType.selectbox"
                          :label="local.caseRelatedRequest.reasons"
                          :value.sync="reasonId"
                          :items="requestTypeRequestReasons"
                          item-text="description"
                          item-value="id"
                          :clearable="false"
                          @change="setSelectedRequestReason"
                        ></naj-input>

                        <naj-input
                          v-if="isRequestReasonTypeTextarea"
                          :type-id="formFieldType.textarea"
                          :label="local.caseRelatedRequest.reasons"
                          :value.sync="requestReasonInput"
                          :counter="requestType.id == 70 ? 2000 : 256"
                          validates="allowed_special_character"
                        ></naj-input>
                      </template>

                      <span v-else>
                        <label class="request-reason-label"> {{ local.caseRelatedRequest.reasons }}</label>
                        <div>
                          <span class="request-reason-text">
                            {{ requestReason.reasonText }}
                          </span>
                        </div>
                      </span>
                    </v-col>

                    <v-col
                      class="text-center"
                      cols="4"
                    >
                      <template v-if="requestType.requestReasonTypeConfig.showRequestReasonAttachment">
                        <label class="request-reason-label"> {{ local.attachments.attachment }}</label>
                        <div
                          v-if="requestReason.sharePointId && requestReason.sharePointFileName"
                          class="d-flex justify-content-center align-items-center file-name h-75"
                        >
                          {{ requestReason.sharePointFileName }}
                        </div>
                        <div
                          v-if="!isViewMode && (!requestReason.sharePointId || !requestReason.sharePointFileName)"
                          class="d-flex justify-content-center h-75 file-upload-icon"
                          :aria-label="local.caseRelatedRequest.btnAttachment"
                          tabindex="0"
                          @click="openRequestReasonAttachmentModal(index)"
                        >
                          <v-icon>
                            mdi-cloud-upload
                          </v-icon>
                        </div>
                      </template>
                    </v-col>

                    <v-col
                      class="d-flex justify-content-end align-items-center"
                      cols="2"
                    >
                      <template v-if="requestReason.isDraft">
                        <naj-button
                          :disabled="isAllowToSaveReason"
                          @click="appendRequestReason(index)"
                        >
                          {{ local.common.save }}
                        </naj-button>
                        <naj-button
                          outlined
                          @click="discardRequestReason(index)"
                        >
                          {{ local.common.cancel }}
                        </naj-button>
                      </template>
                      <template v-else>
                        <v-btn
                          v-if="requestReason.sharePointId && requestReason.sharePointFileName"
                          icon
                          @click="handleViewAttachment(requestReason)"
                        >
                          <v-icon>
                            mdi-eye
                          </v-icon>
                        </v-btn>

                        <v-btn
                          v-if="!isViewMode && !requestReason.isDraft"
                          icon
                          @click="
                            activeReason = index;
                            showDeleteReasonConfirmationModal = true;
                          "
                        >
                          <v-icon color="red">
                            mdi-delete
                          </v-icon>
                        </v-btn>
                      </template>
                    </v-col>
                  </v-row>
                </naj-box>
              </template>
              <v-row>
                <v-col
                  v-if="!isViewMode && formModel.requestReasons.length < 5"
                  cols="1"
                  class="mb-6"
                >
                  <naj-button
                    :disabled="isDraftExist"
                    icon="mdi-plus"
                    @click="handleAddReason"
                  >
                  </naj-button>
                </v-col>
              </v-row>
            </div>

            <naj-box
              v-if="isRequestReasonTypeCheckBox"
              :title="local.caseRelatedRequest.requestReasons"
            >
              <template>
                <v-row
                  v-for="(reason, index) in formModel.requestReasons"
                  :key="index"
                >
                  <v-col cols="12">
                    <naj-input
                      :type-id="formFieldType.checkbox"
                      :value.sync="reason.checked"
                      :label="reason.reasonLabel"
                      :disabled="isViewMode"
                      @change="reasonCheckChanged(index)"
                    ></naj-input>
                  </v-col>
                  <v-col
                    v-if="reason.checked"
                    cols="6"
                    lg="6"
                  >
                    <naj-input
                      :type-id="formFieldType.textarea"
                      :value.sync="reason.reasonText"
                      label=""
                      :disabled="isViewMode"
                      validates="max:2000|allowed_special_character"
                      rows="4"
                    ></naj-input>
                  </v-col>
                  <v-col
                    class="text-center"
                    cols="4"
                  >
                    <template v-if="requestType.requestReasonTypeConfig.showRequestReasonAttachment && reason.checked">
                      <label class="request-reason-label"> {{ local.attachments.attachment }}</label>
                      <div
                        v-if="!isViewMode && (!reason.sharePointId || !reason.sharePointFileName)"
                        class="d-flex justify-content-center h-75 file-upload-icon"
                        aria-label="زر رفع مرفق"
                        tabindex="0"
                        @click="openRequestReasonAttachmentModal(index)"
                      >
                        <v-icon>
                          mdi-cloud-upload
                        </v-icon>
                      </div>
                      <div v-if="!isViewMode && reason.sharePointId && reason.sharePointFileName">
                        <div
                          class="d-flex justify-content-center align-items-center file-name h-75 position-relative file-attachment-container"
                          @click="handleViewAttachment(reason)"
                        >
                          <span class="file-name-text">{{ reason.sharePointFileName }}</span>
                          <v-btn
                            icon
                            small
                            class="attachment-remove-btn"
                            @click.stop="handleDeleteReasonAttachment(index)"
                          >
                            <v-icon
                              color="red"
                              size="18"
                            >
                              mdi-close
                            </v-icon>
                          </v-btn>
                        </div>
                      </div>
                      <div v-if="(reason.sharePointId || reason.sharePointFileName) && isViewMode">
                        <v-btn
                          icon
                          @click="handleViewAttachment(reason)"
                        >
                          <v-icon>
                            mdi-eye
                          </v-icon>
                        </v-btn>
                      </div>
                    </template>
                  </v-col>
                </v-row>
              </template>
            </naj-box>

            <naj-confirmation
              :show-modal="showDeleteReasonConfirmationModal"
              :title="local.caseRelatedRequest.confirmModel.title"
              :message="local.caseRelatedRequest.confirmModel.confirmMessage"
              :ok-btn-label="local.caseRelatedRequest.confirmModel.yes"
              :close-btn-label="local.caseRelatedRequest.confirmModel.no"
              @ok="() => {
                showDeleteReasonConfirmationModal = false;
                handleRemoveReason();
              }"
              @cancel="showDeleteReasonConfirmationModal = false"
            ></naj-confirmation>
          </div>

          <div
            v-if="requestType.showAttachmentSection"
            class="px-3 content-wrapper"
          >
            <h3 class="d-inline-block primary--text font-weight-bold col col-2 mb-4">
              {{ local.attachments.title }}
            </h3>
            <template v-if="formModel.attachments.length > 0">
              <naj-box
                v-for="(file,index) in formModel.attachments"
                :key="index"
                :title="file.fileName"
              >
                <v-row>
                  <v-col cols="10">
                    <label class="request-reason-label"> {{ local.caseRelatedRequest.attachmentName }}</label>
                    <div>
                      <span class="request-reason-text">
                        {{ file.fileName }}
                      </span>
                    </div>
                  </v-col>
                  <v-col
                    class="d-flex justify-content-end align-items-center"
                    cols="2"
                  >
                    <v-btn
                      icon
                      @click="handleViewAttachment(file)"
                    >
                      <v-icon>
                        mdi-eye
                      </v-icon>
                    </v-btn>
                    <v-btn
                      v-if="!isViewMode"
                      icon
                      @click="
                        activeAttachment = index;
                        deleteModalVisibility = true;
                      "
                    >
                      <v-icon color="red">
                        mdi-delete
                      </v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
              </naj-box>
            </template>
            <v-row>
              <v-col
                v-if="!isViewMode"
                cols="1"
                class="mb-6"
              >
                <naj-button
                  icon="mdi-plus"
                  @click="showModal"
                ></naj-button>
              </v-col>
            </v-row>
          </div>

          <naj-box
            v-if="!isReplyMode"
            :title="local.caseFile.tabHeaders.fees"
          >
            <v-row>
              <v-col cols="12">
                <span class="request-reasons-warning">
                  <v-icon color="#FAAD14">
                    mdi-alert
                  </v-icon>
                  {{ local.caseFile.fees.title }}
                </span>
                <div>
                  <span
                    v-if="requestTypeFeesDescription"
                    class="request-reason-text"
                  >
                    {{ requestTypeFeesDescription }}
                  </span>
                  <span
                    v-else
                    class="request-reason-text"
                  >
                    {{ local.caseFile.fees.noFees }}
                  </span>
                </div>
              </v-col>
            </v-row>
          </naj-box>

          <v-row v-if="isNewMode || isEditMode">
            <v-col
              cols="12"
              class="text-left"
            >
              <naj-button
                :disabled="disableSaveAndSendButton"
                @click="validateCaseRelatedRequestThenSubmit"
              >
                {{ isNewMode ? local.common.saveAndSend : editModeSaveAndSendButtonText }}
              </naj-button>
              <naj-confirmation
                :action-loading="loading"
                :show-modal="showSendRequestConfirmationModal"
                :title="isNewMode ? local.common.confirmationSendRequest : local.common.confirmationUpdateRequest"
                :message="isNewMode ? local.caseRelatedRequest.sendRequestConfirmationMessage : local.caseRelatedRequest.updateRequestConfirmationMessage"
                :ok-btn-label="local.common.yes"
                :close-btn-label="local.common.no"
                @ok="onSubmit"
                @cancel="showSendRequestConfirmationModal = false"
              ></naj-confirmation>
            </v-col>
          </v-row>

          <attachment-modal
            ref="attachmentModalComponent"
            :attachment="selectedAttachment"
            :attachment-categories="adjustedAttachmentCategories"
            :attachments-data="attachmentsDataArray"
            modal-button-text=""
            :action-text="local.attachments.addToRequest"
            :view-mode="isViewMode"
            accept="image/*,application/pdf"
            :max-file-size="5"
            @onClose="attachmentModalClosed"
            @onSubmit="submitUploadAttachment"
          ></attachment-modal>

          <naj-confirmation
            :show-modal="deleteModalVisibility"
            color="error"
            :title="local.common.removeConfirmation"
            :message="local.common.removeConfirmationMessage"
            :ok-btn-label="local.common.remove"
            @ok="
              () => {
                deleteModalVisibility = false;
                handleDeleteAttachment();
              }
            "
            @cancel="deleteModalVisibility = false"
          ></naj-confirmation>
        </v-form>
      </div>

      <qaim-evaluation
        v-if="showEvaluationModal"
        :request-evaluations-code="requestEvaluationsCode"
        :ref-num="requestCodeResult"
        :is-lawsuit-request="false"
      >
      </qaim-evaluation>
      <request-reason-modal
        :modal-visibility="showRequestReasonAttachmentModal"
        @addRequestReasonAttachment="addRequestReasonAttachment"
        @onClose="hideRequestReasonAttachmentModal"
      >
      </request-reason-modal>

      <render-attachment-modal
        :attachment-file="selectedFile"
        :modal-visibility="showViewAttachmentModal"
        @modalClose="hideViewAttachmentModal"
      >
      </render-attachment-modal>

      <naj-confirmation
        :show-modal="showInformationModal"
        :max-width="750"
        :is-persistent="true"
        :title="local.caseRelatedRequest.successAlertMessage"
        :close-btn-label="local.common.close"
        @cancel="closeInformationModal"
      >
        <template slot="message-content">
          <v-row>
            <v-col>
              <naj-alert type="success">
                <template slot="content">
                  {{ isNewMode ? local.caseRelatedRequest.requestSendSuccessfully : local.caseRelatedRequest.requestUpdateSuccessfully }}، {{ local.caseRelatedRequest.requestCode }}
                  {{ requestCodeResult }}
                </template>
              </naj-alert>
            </v-col>
          </v-row>
        </template>
      </naj-confirmation>

      <naj-confirmation
        :show-modal="showValidationSummaryModal"
        :max-width="1000"
        :is-persistent="true"
        :title="`${local.caseRelatedRequest.validationSummaryTitle}`"
        :close-btn-label="local.common.close"
        @cancel="
          () => {
            showValidationSummaryModal = false;
            validationSummaryText = '';
          }
        "
      >
        <template slot="message-content">
          <v-row>
            <v-col>
              <naj-alert type="error">
                <template slot="content">
                  <div v-html="validationSummaryText" />
                </template>
              </naj-alert>
            </v-col>
          </v-row>
        </template>
      </naj-confirmation>
    </page-content>
  </fragment>
</template>

<script>
import DynamicForm from '@/modules/global/components/dynamic-form/index';
import { createNamespacedHelpers } from 'vuex';
import { RequestTypes, RequestTypesNames, mode, casePartyRoleEnum, requestEvaluationsCodes, requestEvaluationsCodesIds, RequestTypesIds, waiverAttachmentTypes } from '@/helpers/enum';
import { resetScroll, uuidGenerator } from '@/helpers/utils';
import AttachmentModal from '@/components/shared/attachment-modal/index';
import RequestReasonModal from '@/components/shared/request-reason-modal/index';
import qaimEvaluation from '@/modules/lawsuit/components/qaim-evaluations';
import RenderAttachmentModal from '@/components/shared/attachment-modal/render-attachment-modal';

const { mapActions } = createNamespacedHelpers('CasesModule');
const { mapState: mapLawsuitState } = createNamespacedHelpers('LawsuitModule');
const { mapState: mapLUTState, mapActions: mapLUTActions } = createNamespacedHelpers('LookupsModule');
const { mapState: mapGlobalState, mapActions: mapGlobalActions } = createNamespacedHelpers('GlobalModule');

export default {
  components: {
    DynamicForm,
    AttachmentModal,
    RequestReasonModal,
    qaimEvaluation,
    RenderAttachmentModal,
  },
  data() {
    return {
      requestEvaluationsCode: null,
      showEvaluationModal: false,
      mode,
      request: {},
      showSendRequestConfirmationModal: false,
      loading: false,
      RequestTypes,
      searchQuery: {
        objectKey: null,
        caseCode: null,
        caseYear: null,
        courtId: null,
        isPega: false,
        casePartyType: null,
        socialTypeId: null,
        socialId: null,
        commercialRegistrationNumber: null,
        organizationId: null,
        name: null,
      },
      requestCustomData: null,
      caseParties: [],
      userCasePartyRoleInCase: {},
      isLoggedInUserExistsAsCaseParty: false,
      requestTypeFeesDescription: null,
      requestTypeRequestReasons: [],
      selectedRequestReason: {},
      tableHeader: [
        {
          value: 'caseCode',
          text: this.local.caseReply.tableHeader.caseCode,
        },
        {
          value: 'caseDate',
          text: this.local.caseReply.tableHeader.caseDate,
        },
        {
          value: 'courtName',
          text: this.local.caseReply.tableHeader.court,
        },
        {
          value: 'subCircleName',
          text: this.local.caseReply.tableHeader.circle,
        },
        {
          value: 'caseStatusName',
          text: this.local.caseReply.tableHeader.caseStatusName,
        },
        {
          value: 'caseCategoryName',
          text: this.local.caseReply.tableHeader.caseCategoryName,
        },
        {
          value: 'caseTypeName',
          text: this.local.caseReply.tableHeader.caseType,
        },
      ],
      casePartyTableHeader: [
        {
          value: 'caseCode',
          text: 'اسم طرف الدعوى',
        },
        {
          value: 'caseDate',
          text: 'نوع طرف الدعوى',
        },
        {
          value: 'courtName',
          text: 'الرقم الوطني',
        },
        {
          value: 'subCircleName',
          text: 'نوع الاقامة',
        },
        {
          value: 'caseStatusName',
          text: 'العنوان',
        },
        {
          value: 'caseCategoryName',
          text: 'رقم الجوال',
        },
      ],
      formModel: {
        relatedPartiesInfo: [],
        requestType: 92,
        requestReasons: [],
        attachments: [],
        representative: {},
      },
      // reasons
      showDeleteReasonConfirmationModal: false,
      activeReason: null,
      reasonId: null,
      requestReasonInput: '',
      requestReasonsHeaders: [
        {
          text: 'السبب',
          value: 'reasonText',
          width: '60%',
        },
        {
          text: 'اسم المرفق',
          value: '_fileName',
          width: '20%',
        },
        {
          value: 'action',
          text: 'الإجراء',
        },
      ],
      // attachments
      selectedAttachment: null,
      deleteModalVisibility: false,
      activeAttachment: null,
      showRequestReasonAttachmentModal: false,
      showInformationModal: false,
      confirmationIconDetails: {
        icon: 'mdi-information',
        color: '#F7CE44',
        size: '150',
      },
      informationIconDetails: {
        icon: 'mdi-check-circle',
        color: 'var(--app-primary-color)',
        size: '150',
      },
      requestCodeResult: '',
      showValidationSummaryModal: false,
      validationSummaryText: '',
      showViewAttachmentModal: false,
      selectedFile: {},
      socialId: null,
      partyInfo: {},
      casePartyRoleEnum,
      isValidApplicantToApply: false,
      isValidCaseData: false,
      user: {},
    };
  },
  computed: {
    ...mapLawsuitState({ attachmentsData: state => state.attachments.data }),
    ...mapLUTState(['courts', 'attachmentCategories']),
    ...mapGlobalState(['userInfo', 'qaimConfig']),
    adjustedAttachmentCategories() {
      if ((this.requestType || {}).id === this.RequestTypes['waiver-request'].id) {
        const allowedIds = [waiverAttachmentTypes.otherAttachment, waiverAttachmentTypes.repairReportAttachment, waiverAttachmentTypes.conditionValideAttachment];
        if (allowedIds.length) {
          return this.attachmentCategories.filter(c => allowedIds.includes(c.id));
        }
      }
      return this.attachmentCategories;
    },
    attachmentsDataArray() {
      return Object.values(this.attachmentsData);
    },
    requestType() {
      return this.RequestTypes[this.$route.params.requestType] || {};
    },
    isFromNonCaseParty() {
      return this.$route.query.isFromNonCaseParty == 'true';
    },
    parentRequestObjectKey() {
      return this.$route.query.parentRequestObjectKey;
    },
    isViewMode() {
      return this.formModel && this.formModel.mode === mode.view;
    },
    isEditMode() {
      return this.formModel && this.formModel.mode === mode.update;
    },
    isNewMode() {
      return this.$route.params.requestObjectKey === 'new';
    },
    isPega() {
      const courtId = this.$route.query.c;
      const court = this.courts.find(c => +c.id === +courtId);
      const isPegaCourt = court && court.isPega;
      return isPegaCourt || this.searchQuery.isPega || this.$route.query.isPega == 'true';
    },
    requireSearch() {
      return !this.$route.params.caseObjectKey;
    },
    isDraftExist() {
      return (this.formModel.requestReasons || []).some(a => a.isDraft);
    },
    formModelCaseObjectKey() {
      return (this.formModel.case || {}).objectKey;
    },
    formModelRequestObjectKey() {
      return (this.formModel || {}).objectKey;
    },
    casePartiesSelectboxDisabled() {
      return this.isViewMode || (this.formModel.applicantCasePartyId && this.isLoggedInUserExistsAsCaseParty);
    },
    prosecutorOrDefendantCaseParties() {
      return this.caseParties;
    },
    isRequestReasonTypeTextarea() {
      return (
        this.requestType.requestReasonTypeConfig.fieldType == this.formFieldType.textarea ||
        this.selectedRequestReason.isOtherReason
      );
    },
    isRequestReasonTypeCheckBox() {
      return this.requestType.requestReasonTypeConfig.fieldType == this.formFieldType.checkbox;
    },
    isRequestReasonTypeSelectbox() {
      return this.requestType.requestReasonTypeConfig.fieldType == this.formFieldType.selectbox;
    },
    isReplyMode() {
      return (
        this.requestType.id === RequestTypesNames.waiverOfAppealRequest.id &&
        this.isViewMode &&
        this.$route.meta.replyToRequest === true
      );
    },
    isAllowToSaveReason() {
      const isSpecialCharacterErrorExist = this.errors.items.some(r => r.rule === 'allowed_special_character');
      if (this.requestType.id === RequestTypesNames.verdictObjection.id)
        return (
          this.requestReasonInput.trim().length > 2000 ||
          this.requestReasonInput.trim().length < 1 ||
          isSpecialCharacterErrorExist
        );
      else
        return (
          this.requestReasonInput.trim().length > 256 || this.requestReasonInput.trim().length < 1 || isSpecialCharacterErrorExist
        );
    },
    routeRequestObjectKey() {
      return this.$route.query.requestObjectKey;
    },
    routeCaseObjectKey() {
      return this.$route.params.caseObjectKey;
    },
    disableSaveAndSendButton() {
      return this.formModel.isApplicantEligibleToApply === false;
    },
    editModeSaveAndSendButtonText() {
      if ((this.requestType || {}).id === this.RequestTypes['waiver-request'].id) {
        return this.local.common.resend;
      }
      return this.local.common.saveAndSend;
    },
    isCheckButtonDisabled() {
      return !this.socialId || this.socialId.toString().trim() === '';
    },
    isOrganizationUser() {
      return this.user && this.user.isOrganizationUser;
    },
    isRepresentative() {
      return this.requestType.id === this.RequestTypes['add-representative'].id;
    },
    showSelectedPartyToRepresent() {
      return !this.routeCaseObjectKey && this.isValidCaseData && !this.isOrganizationUser && this.isNewMode && this.isRepresentative;
    },
  },
  watch: {
    formModelCaseObjectKey(value) {
      if (this.requestType.showDefaultRequestApplicantSection && value) {
        this.loading = true;
        const params = {
          isPega: this.isPega,
          objectKey: this.formModelCaseObjectKey,
        };
        const params2 = {
          isPega: this.isPega,
          objectKey: this.formModelCaseObjectKey,
          requestObjectKey: this.routeRequestObjectKey ? this.routeRequestObjectKey : this.formModelRequestObjectKey,
        };
        if (this.isNewMode && !this.routeRequestObjectKey) {
          Promise.all([this.getCaseRequestApplicants(params), this.getUserCasePartyRoleInCase(params)])
            .then((res) => {
              this.caseParties = !this.isViewMode
                ? res[0].data.requestApplicants
                : [...res[0].data.prosecutors, ...res[0].data.defendants];
              this.userCasePartyRoleInCase = res[1].data;
              this.setRelatedPartyAsRequestApplicant();
            })
            .catch(err => this.toaster.error(err.message))
            .finally(() => (this.loading = false));
        } else {
          Promise.all([this.getCaseRequestApplicant(params2), this.getUserCasePartyRoleInCase(params2)])
            .then((res) => {
              this.caseParties = res[0].data.requestApplicants;
              this.userCasePartyRoleInCase = res[1].data;
              this.setRequestPartyAsRequestApplicant(this.caseParties[0].casePartyId);
            })
            .catch(err => this.toaster.error(err.message))
            .finally(() => (this.loading = false));
        }
      }
      if ((this.isRequestReasonTypeSelectbox || this.isRequestReasonTypeCheckBox) && value) {
        this.loadRequestTypeRequestReasons();
      }
    },

    'formModel.applicantCasePartyId': function (value) {
      if (value && this.isPega) {
        this.loadRequestTypeFeesDescription();
      }
    },
  },
  async created() {
    if (this.$route.params.caseObjectKey) {
      await this.userHasPermissionCaseRelatedRequest(this.$route.params.caseObjectKey).catch(error => this.$router.push('/'));
    }
  },
  mounted() {
    this.getUserInfo().then((user) => {
      this.user = user;
    });
    this.getCourts().then(() => {
      this.getData();
    });
    this.getAttachmentCategories();
    this.$set(this.formModel, 'requestType', this.requestType.id);
    if (this.$route.params.caseObjectKey) {
      this.isValidApplicantToApply = true;
      this.isValidCaseData = true;
      this.searchOnBehalfUser();
    }
  },
  methods: {
    ...mapGlobalActions(['getUserInfo']),
    ...mapLUTActions(['getCourts', 'getAttachmentCategories']),
    ...mapActions([
      'getCaseWithSpecificParty',
      'validateCaseRelatedRequest',
      'saveCaseRelatedRequest',
      'updateCaseRelatedRequest',
      'getCaseRelatedRequests',
      'uploadAttachment',
      'getUserCasePartyRoleInCase',
      'getCaseRequestApplicants',
      'getCaseRequestApplicant',
      'getRequestFeesInfo',
      'getRequestTypeRequestReasons',
      'userHasPermissionCaseRelatedRequest',
    ]),
    filterChanged() {
      this.$set(this.formModel, 'case', null);
      this.$set(this.formModel, 'applicantCasePartyId', null);
      this.isValidCaseData = false;
      this.isValidApplicantToApply = false;
      this.partyInfo = {};
      this.socialId = null;
    },
    filterCourtChanged(courtId) {
      this.filterChanged();
      const court = this.courts.find(c => +c.id === +courtId);
      this.searchQuery.isPega = court && court.isPega;
    },
    searchOnBehalfUser() {
      this.searchQuery.objectKey = this.$route.params.caseObjectKey;
      this.searchQuery.isPega = this.isPega;
      this.onSearch();
    },
    getData() {
      const requestObjectKey = this.$route.params.requestObjectKey;

      if (requestObjectKey !== 'new') {
        this.loading = true;
        this.getCaseRelatedRequests({
          requestObjectKey,
          isPega: this.isPega,
        })
          .then(({ data }) => {
            this.isValidCaseData = true;
            this.isValidApplicantToApply = true;
            this.request = {
              id: data.id,
              status: data.requestStatusName,
              requestCode: data.requestCode,
              requestDate: data.requestDate,
            };
            this.formModel = {
              ...data,
              objectKey: this.$route.params.requestObjectKey,
              requestType: this.requestType.id,
            };
            this.requestCustomData = {
              isFromNonCaseParty: this.isFromNonCaseParty,
              parentRequestObjectKey: this.parentRequestObjectKey,
            };
          })
          .catch(err => this.toaster.error(err.message))
          .finally(() => (this.loading = false));
      }
    },
    onSearch() {
      if (!this.showEvaluationModal) {
        Promise.all([
          this.requireSearch ? this.$refs.searchFormRef.$validator.validateAll() : Promise.resolve(true),
          this.$refs.searchInfo ? this.$refs.searchInfo.$validator.validateAll() : Promise.resolve(true),
        ]).then((results) => {
          if (results.every(i => i)) {
            this.loading = true;
            this.$set(this.formModel, 'isPega', this.isPega);

            this.getCaseWithSpecificParty(this.searchQuery)
              .then(({ data }) => {
                if (data.caseCode) {
                  this.$set(this.formModel, 'case', {
                    ...this.searchQuery,
                    ...data,
                  });
                  this.requestCustomData = {
                    ...data,
                    partyType: this.searchQuery.partyType,
                  };

                  if (!this.routeCaseObjectKey) {
                    this.isValidCaseData = true;
                  }
                  if (this.isOrganizationUser) {
                    this.isValidCaseData = true;
                    this.isValidApplicantToApply = true;
                  }
                  if (!this.isRepresentative) {
                    this.isValidCaseData = true;
                    this.isValidApplicantToApply = true;
                  }
                } else {
                  this.toaster.error(this.local.caseRelatedRequest.notFound);
                }
              })
              .catch(err => !err.handled && this.toaster.error(err.message))
              .finally(() => (this.loading = false));
          }
        });
      }
    },
    validateCaseRelatedRequestThenSubmit() {
      if ((this.requestType || {}).id === this.RequestTypes['waiver-request'].id && !this.isNewMode) {
        var hasConditionValidAttachment = this.formModel.attachments.some(a => a.fileCategory == waiverAttachmentTypes.conditionValideAttachment);
        if (!hasConditionValidAttachment) {
          this.toaster.error(this.local.caseRelatedRequest.waiverRequestConditionValidAttachmentRequired);
          return;
        }
      }
      this.$refs.requestFormRef.$validator.validateAll().then((valid) => {
        if (valid) {
          this.loading = true;
          const payload = {
            objectKey: this.formModel.objectKey || uuidGenerator(),
            data: {
              ...this.formModel,
              isPega: this.isPega,
              requestType: this.requestType,
            },
          };
          this.validateCaseRelatedRequest(payload)
            .then(() => {
              this.showSendRequestConfirmationModal = true;
            })
            .catch(err => this.toaster.error(err))
            .finally(() => (this.loading = false));
        } else {
          resetScroll();
        }
      });
    },
    onSubmit() {
      this.loading = true;
      const payload = {
        isPega: this.isPega,
        objectKey: this.formModel.objectKey || uuidGenerator(),
        data: {
          ...this.formModel,
          isPega: this.isPega,
          case: {
            ...this.formModel.case,
            id: this.formModel.case.caseId || this.formModel.case.id,
          },
        },
      };
      if (this.isNewMode) {
        this.saveCaseRelatedRequest(payload)
          .then((result) => {
            this.showSendRequestConfirmationModal = false;
            this.requestCodeResult = result.data.requestCode;
            this.showInformationModal = true;
            this.requestEvaluationsCode = this.getRequestEvaluationsCode();
          })
          .catch((err) => {
            if (err.handled) {
              this.showSendRequestConfirmationModal = false;
              this.showValidationSummaryModal = true;
              this.validationSummaryText = err.message;
            } else {
              this.toaster.error(err.message);
            }
          })
          .finally(() => (this.loading = false));
      } else if (this.isEditMode) {
        this.updateCaseRelatedRequest(payload)
          .then(() => {
            this.showSendRequestConfirmationModal = false;
            this.requestCodeResult = this.formModel.requestCode || this.request.requestCode;
            this.showInformationModal = true;
          })
          .catch((err) => {
            if (err.handled) {
              this.showSendRequestConfirmationModal = false;
              this.showValidationSummaryModal = true;
              this.validationSummaryText = err.message;
            } else {
              this.toaster.error(err.message);
            }
          })
          .finally(() => (this.loading = false));
      }
    },
    // reasons
    handleAddReason() {
      this.formModel.requestReasons.push({
        reasonId: this.reasonId,
        reasonText: this.requestReasonInput,
        isDraft: true,
      });
    },
    appendRequestReason(index) {
      const duplicateReasonExists = this.formModel.requestReasons.some(
        r => r.reasonText == this.requestReasonInput && r.reasonId == this.reasonId
      );
      if (duplicateReasonExists) {
        this.toaster.error(this.local.caseRelatedRequest.canNotAddDuplicateReason);
        return;
      }
      this.formModel.requestReasons[index].reasonText = this.requestReasonInput;
      this.formModel.requestReasons[index].reasonId = this.reasonId || 1;
      this.formModel.requestReasons[index].isDraft = false;
      this.resetRequestReasonFields();
    },
    discardRequestReason(index) {
      this.formModel.requestReasons.splice(index, 1);
      this.resetRequestReasonFields();
    },
    handleRemoveReason(index = this.activeReason) {
      this.formModel.requestReasons.splice(index, 1);
    },
    // attachments
    showModal(id) {
      this.$refs.attachmentModalComponent.showModal(id);
    },
    handleViewAttachment(file) {
      this.$set(this, 'selectedFile', {
        name: file.fileName || file.sharePointFileName,
        isImage: file.extension.startsWith('image'),
        sharePointId: file.sharePointId ?? file.sharepointId,
      });
      this.showViewAttachmentModal = true;
    },
    handleDeleteAttachment(index = this.activeAttachment) {
      this.formModel.attachments.splice(index, 1);
    },
    attachmentSelected(attachment) {
      this.selectedAttachment = attachment;

      if (attachment && this.$refs.attachmentModalComponent) {
        this.$refs.attachmentModalComponent.showModal();
      }
    },
    attachmentModalClosed() { },
    submitUploadAttachment({ attachment }) {
      this.loading = true;
      const reader = new FileReader();
      const self = this;

      reader.onloadend = () => {
        var b64 = reader.result.replace(/^data:.+;base64,/, '');

        self
          .uploadAttachment({
            classification: 0,
            documentMetadata: {
              caseId: 0,
              caseCode: 0,
              requestId: 0,
              category: attachment.category,
              fileCategory: attachment.category,
            },
            fileCategory: attachment.category,
            extension: attachment.extension,
            base64FileContent: b64,
          })
          .then(({ data }) => {
            self.formModel.attachments.push({
              fileName: attachment.name,
              extension: attachment.extension,
              sharePointId: data,
              fileCategory: attachment.category,
            });

            this.$refs.attachmentModalComponent.closeModal();
          })
          .catch(err => this.toaster.error(err.message))
          .finally(() => (this.loading = false));
      };

      reader.readAsDataURL(attachment.fileObject);
    },
    getMappedIndexReasonText(index) {
      switch (index) {
        case 0:
          return 'السبب الأول';
        case 1:
          return 'السبب الثاني';
        case 2:
          return 'السبب الثالث';
        case 3:
          return 'السبب الرابع';
        case 4:
          return 'السبب الخامس';
        default:
          return '';
      }
    },
    addRequestReasonAttachment(file) {
      this.formModel.requestReasons[this.activeReason].sharePointId = file.sharePointId;
      this.formModel.requestReasons[this.activeReason].sharePointFileName = file.sharePointFileName;
      this.formModel.requestReasons[this.activeReason].extension = file.extension;
    },
    openRequestReasonAttachmentModal(index) {
      this.activeReason = index;
      this.showRequestReasonAttachmentModal = true;
    },
    hideRequestReasonAttachmentModal() {
      this.activeReason = null;
      this.showRequestReasonAttachmentModal = false;
    },
    closeInformationModal() {
      if (this.requestEvaluationsCode) {
        this.showEvaluationModal = true;
      }
      else {
        this.$router.push({ path: '/' });
      }
      this.showInformationModal = false;
    },
    surveyClosed() {
      this.$router.push({ path: '/' });
    },
    setRelatedPartyAsRequestApplicant() {
      const selectedParty = this.prosecutorOrDefendantCaseParties.find(
        cp => cp.partyId == this.userCasePartyRoleInCase.partyId
      );
      if (selectedParty) {
        this.isLoggedInUserExistsAsCaseParty = true;
        this.$set(this.formModel, 'applicantCasePartyId', this.formModel.applicantCasePartyId || selectedParty.casePartyId);
      }
    },
    setRequestPartyAsRequestApplicant(casePartyId) {
      this.isLoggedInUserExistsAsCaseParty = true;
      this.$set(this.formModel, 'applicantCasePartyId', casePartyId);
    },
    loadRequestTypeFeesDescription() {
      this.loading = true;

      const params = {
        requestTypeId: this.requestType.id,
        caseObjectKey: this.formModelCaseObjectKey,
        applicantCasePartyId: this.formModel.applicantCasePartyId,
      };
      this.getRequestFeesInfo(params)
        .then(({ data }) => {
          this.requestTypeFeesDescription = data.description;
        })
        .catch(() => { })
        .finally(() => {
          this.loading = false;
        });
    },
    loadRequestTypeRequestReasons() {
      this.loading = true;
      this.getRequestTypeRequestReasons(this.requestType.id)
        .then(({ data }) => {
          this.requestTypeRequestReasons = this.isNewMode ? data.filter(reason => !reason.isOtherReason) : data;

          if (this.isRequestReasonTypeCheckBox) {
            if (this.isNewMode) {
              this.formModel.requestReasons = this.requestTypeRequestReasons.map(reason => ({
                reasonId: reason.id,
                reasonText: '',
                checked: false,
                isDraft: false,
                reasonLabel: reason.description,
              }));
            } else {
              this.formModel.requestReasons.map((reason) => {
                const matchedReason = this.requestTypeRequestReasons.find(r => r.id === reason.reasonId);
                if (matchedReason) {
                  reason.reasonLabel = matchedReason.description;
                }
                return reason;
              });
            }
          }
        })
        .catch(() => { })
        .finally(() => {
          this.loading = false;
        });
    },
    setSelectedRequestReason() {
      this.selectedRequestReason = this.requestTypeRequestReasons.find(r => r.id == this.reasonId) || {};
      if (!this.selectedRequestReason.isOtherReason) {
        this.requestReasonInput = this.selectedRequestReason.description;
      } else {
        this.requestReasonInput = '';
      }
    },
    resetRequestReasonFields() {
      this.requestReasonInput = '';
      this.reasonId = null;
      this.selectedRequestReason = {};
    },
    hideViewAttachmentModal() {
      this.showViewAttachmentModal = false;
    },
    reasonCheckChanged(index) {
      const selectedReason = this.formModel.requestReasons[index];
      if (!selectedReason.checked) {
        this.formModel.requestReasons[index].reasonText = null;
        this.formModel.requestReasons[index].sharePointFileName = null;
        this.formModel.requestReasons[index].sharePointId = null;
      }
    },
    handleDeleteReasonAttachment(index) {
      this.$set(this.formModel.requestReasons[index], 'sharePointId', null);
      this.$set(this.formModel.requestReasons[index], 'sharePointFileName', null);
      this.$set(this.formModel.requestReasons[index], 'extension', null);
      this.$forceUpdate();
    },
    getRequestEvaluationsCode() {
      const specialRequests = [
        RequestTypesNames.stopResumeLawsuitRequest.id,
      ];

      if (specialRequests.includes(this.requestType.id)) {
        const requestTypeName = RequestTypesIds[this.requestType.id];
        let formValueProperty = null;

        if (this.requestType.id == RequestTypesNames.stopResumeLawsuitRequest.id) {
          formValueProperty = this.formModel.StopOrContinue?.Value;
        }

        const requestEvaluationsCodeName = (this.qaimConfig[requestTypeName] || {})[formValueProperty];
        return this.qaimConfig.requestEvaluationsCodes[requestEvaluationsCodeName];
      }

      return this.qaimConfig.requestEvaluationsCodes[this.qaimConfig.requestEvaluationsCodesIds[this.requestType.id]];
    },
    findPartyByType(allParties) {
      const targetId = this.socialId?.toString().trim();

      return allParties.find(p =>
        (p.socialId?.toString().trim() === targetId) ||
        (p.commercialRegisterNo?.toString().trim() === targetId) ||
        (p.unifiedNumber?.toString().trim() === targetId)
      );
    },
    setPartyInfo(party) {
      this.$set(this, 'partyInfo', {
        name: party.name,
        identityNumber: party.socialId || party.unifiedNumber || party.commercialRegisterNo,
        partyType: party.partyTypeName,
        caseRole: party.casePartyRoleName,
      });
      this.$set(this, 'isValidApplicantToApply', true);
      this.setRequestPartyAsRequestApplicant(party.casePartyId);
    },
    showPartyNotFoundError() {
      this.toaster.error(this.local.lawsuitRequest.requestInfo.partyNameNotFound);
      this.socialId = null;
      this.unifiedNumber = null;
      this.partyInfo = {};
    },
    getPartyByCaseCode() {
      this.isValidApplicantToApply = false;
      this.partyInfo = {};
      this.getCaseRequestApplicants({
        isPega: true,
        objectKey: this.formModelCaseObjectKey,
      })
        .then(({ data }) => {
          if (!data) {
            this.showPartyNotFoundError();
            return;
          }

          const validRoles = [
            casePartyRoleEnum.prosecutor,
            casePartyRoleEnum.defendant,
            casePartyRoleEnum.appellant,
            casePartyRoleEnum.appellee,
          ];

          const allParties = [...data.prosecutors, ...data.defendants].filter(x => validRoles.includes(x.casePartyRole));

          if (allParties.length === 0) {
            this.showPartyNotFoundError();
            return;
          }

          const party = this.findPartyByType(allParties);

          if (party) {
            this.setPartyInfo(party);
          } else {
            this.showPartyNotFoundError();
          }
        }).catch(() => { });
    },
  },
};
</script>
<style lang="scss" scoped>
::v-deep {
  .request-info-root {
    background-color: var(--app-light-secondary-color);
    padding: 10px 20px;
    border-radius: 5px;
    border: 1px solid var(--app-primary-color);
    margin-bottom: 20px;

    > div {
      min-width: 170px;

      > b {
        display: flex;
        width: 100%;
        justify-content: space-between;
        font-weight: 700;
        margin-bottom: 5px;

        > span {
          margin-right: 0;
          margin-left: 5px;
        }
      }
    }
  }
}

.request-reasons-warning {
  font-size: 16px;
  color: #faad14;
}

.request-reason-label {
  font-size: 20px;
}

.request-reason-text {
  font-size: 15px;
  color: #484952a0;
}

.content-wrapper {
  border: 1px solid #e4e5e6 !important;
  margin-bottom: 25px;
}

.file-name {
  font-size: 18px;
}

.file-upload-icon {
  cursor: pointer;
}

.file-attachment-container {
  cursor: pointer;
  border: 1px solid #e4e5e6;
  border-radius: 4px;
  padding: 8px;
  background-color: #f9f9f9;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f0f0f0;
  }
}

.file-name-text {
  flex-grow: 1;
  text-align: center;
}

.attachment-remove-btn {
  position: absolute !important;
  top: -5px;
  right: -5px;
  background-color: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}
</style>
